import {SuccessResult} from "../model/connect";
import {UserInfo} from "~/app/core/model/user-info";
import {UserAuthType} from "~/app/core/model/user-auth-type";
import {UserLoginResult} from "~/app/core/model/user-login-result";
import {toDateStr} from "~/app/core/model/date-str";
import {StorageService} from "~/app/core/storage.service";
import {InitInfo} from "~/app/core/model/init-info";
import {InterfaceUserProfileService} from "~/app/core/user-profile-service/interface-user-profile-service";
import {Settings} from "~/app/core/model/settings";
import * as Log from "~/app/core/log";
import {SettingsService} from "~/app/core/settings.service";
import {RPCService} from "~/app/core/rpc.service";
import {AccessToken} from "~/app/core/model/rpc/access-token";
import {UserInfoRPC} from "~/app/core/model/rpc/user-info-rpc";
import {LocationInfoRPC} from "~/app/core/model/rpc/location-info-rpc";
import {UserProperty} from "~/app/core/model/user-property";
import {DeviceSpecs} from "~/app/core/model/rpc/device-specs";
import {NetworkService} from "~/app/core/network.service";
import OneSignal from "onesignal-cordova-plugin";

export class HttpUserProfileService implements InterfaceUserProfileService {
    constructor(
        private readonly networkService: NetworkService,
        private readonly settingsService: SettingsService,
        private readonly storageService: StorageService,
        private readonly rpcService: RPCService
    ) {
    }

    private get settings(): Settings {
        return this.settingsService.loadSettings();
    }

    initService() {
        const userInfo = this.storageService.loadUserInfo(this.settings.env);
        Log.debug(Log.Tag.HttpUserProfileService, "LOAD USER INFO");
        Log.debug(Log.Tag.HttpUserProfileService, userInfo);
    }

    get userInfo(): UserInfo {
        return this.storageService.loadUserInfo(this.settings.env);
    }
     getUserReferralCode(): Promise<string> {
      return this.rpcService.shareCode();
    }

    get deviceInfo(): InitInfo {
        return this.storageService.loadInitInfo();
    }

    setWelcomed() {
        let initInfo = this.deviceInfo;
        if (!initInfo) {
            initInfo = {
                needWelcome: false,
                showLogin: false
            };
        }
        initInfo.needWelcome = false;
        this.storageService.storeInitInfo(initInfo);
    }

    isWelcomed(): boolean {
        const initInfo = this.deviceInfo;
        return initInfo && initInfo.needWelcome !== true;
    }

    captureLocation(location: LocationInfoRPC): Promise<void> {
        return this.rpcService.UpdateZip(location);
    }

    updateUserProperty(property: UserProperty, value: string): Promise<void> {
        Log.debug(Log.Tag.InteractionService, "Set user property: " + property + " = " + value);
        // return firebase.analytics.setUserProperty({
        //     key: property,
        //     value: value
        // });
        return Promise.resolve();
    }

    testResetWelcomed(): void {
        this.storageService.storeInitInfo(null);
    }

    testResetFirstRedeem(): void {
        const userInfo: UserInfo = this.storageService.loadUserInfo(this.settings.env);
        userInfo.firstRedeemDate = null;
        this.storageService.storeUserInfo(userInfo, this.settings.env);
    }

    showRegister(): void {
        const initInfo = this.deviceInfo;
        if (initInfo) {
            initInfo.showLogin = false;
            this.storageService.storeInitInfo(initInfo);
        }
    }

    testDeleteUser(): Promise<SuccessResult> {
        const userInfo: UserInfo = this.storageService.loadUserInfo(this.settings.env);
        if (userInfo && userInfo.authToken) {
            return new Promise((resolve, reject) => {
                this.rpcService.DeleteUser().then((result) => {
                    this.showRegister();
                    this.logout();
                    // done deleting
                    resolve({success: true});
                }, (error) => {
                    // error, but ignore
                    reject(error);
                });
            });
        }
        return Promise.resolve({success: true});
    }

    isAuthenticated(): boolean {
        const userInfo = this.userInfo;
        return userInfo && userInfo.authToken != null;
    }

    needsValidation(): boolean {
        const userInfo = this.userInfo;
        return userInfo && userInfo.needsValidation;
    }

    initDevice(deviceSpecs: DeviceSpecs): Promise<InitInfo> {
        const initInfo = this.storageService.loadInitInfo();
        if (initInfo) {
            return Promise.resolve(initInfo);
        } else {
            return new Promise((resolve, reject) => {
                // set some defaults in case the request doesn't return
                this.storageService.storeInitInfo({
                    showLogin: false,
                    needWelcome: true
                });
                Log.debug(Log.Tag.HttpUserProfileService, "INITDEVICE");
                this.rpcService.InitDevice(deviceSpecs).then((result) => {
                    Log.trace(Log.Tag.HttpUserProfileService, "INITDEVICE RESPONSE:");
                    Log.trace(Log.Tag.HttpUserProfileService, result);
                    this.storageService.storeInitInfo({
                        showLogin: result.show_login === true,
                        needWelcome: result.show_login !== true || true
                    });
                    if (result.access_token && false) { // don't auto-login based on device
                        // load user info
                        this.updateUserProperty(UserProperty.Authenticated, !result.access_token ? "false" : "true");
                        this.storageService.storeUserInfo({
                            auth: UserAuthType.WhutsFree,
                            authToken: result.access_token,
                            id: null,
                            needsValidation: false,
                            firstName: null,
                            lastName: null,
                            email: null,
                            photoUrl: null
                        }, this.settings.env);
                        this.initUserInfo().then((info) => {
                            resolve(this.storageService.loadInitInfo());
                        }, (error) => {
                            resolve(this.storageService.loadInitInfo());
                        });
                    } else {
                        resolve(this.storageService.loadInitInfo());
                    }
                }, (error) => {
                    Log.error(Log.Tag.HttpUserProfileService, "INITDEVICE ERROR:");
                    Log.debug(Log.Tag.HttpUserProfileService, error);
                    resolve(this.storageService.loadInitInfo());
                });
            });
        }
    }

  setReferralCode(referralCode: string) {
    this.storageService.storeCode(referralCode);
  }
  getReferralCode(): string {
    return this.storageService.loadCode();
  }

    loginWithFacebook(pushToken: string): Promise<UserLoginResult> {
        return new Promise((resolve, reject) => {
            // SocialLogin.loginWithFacebook((result) => {
            //     Log.trace(Log.Tag.HttpUserProfileService, result);
            //     if (result.authToken) {
            //         this.rpcService.FacebookAuth({
            //             id_token: result.authToken,
            //             push_token: pushToken
            //         }).then(this.handleAccessTokenResult(UserAuthType.Facebook, null, resolve, reject),
            //             (error) => { reject(error); }
            //         );
            //     } else {
                    resolve({
                        success: false,
                        validated: false
                    });
            //     }
            // });
        });
    }

    loginWithGoogle(pushToken: string): Promise<UserLoginResult> {
        return new Promise((resolve, reject) => {
            // SocialLogin.loginWithGoogle((result) => {
            //     if (result.code === 0 && result.id) {
            //         Log.debug(Log.Tag.HttpUserProfileService, "token: " + result.authToken);
            //         this.rpcService.GoogleAuth({
            //             id_token: result.authToken,
            //             push_token: pushToken
            //         }).then(this.handleAccessTokenResult(UserAuthType.Google, null, resolve, reject),
            //             (error) => { reject(error); }
            //         );
            //     } else {
                    resolve({
                        success: false,
                        validated: false
                    });
            //     }
            // });
        });
    }

    loginWithApple(pushToken: string): Promise<UserLoginResult> {
        return new Promise((resolve, reject) => {
            // signInWithApple({ scopes: ["EMAIL", "FULLNAME"] }).then((credential) => {
            //     Log.debug(Log.Tag.HttpUserProfileService, "Signed in, user: " + credential.user);
            //     Log.debug(Log.Tag.HttpUserProfileService, credential);
            //     if (credential.user) {
            //         let firstname = null;
            //         let lastname = null;
            //         if (credential.fullName) {
            //             const space = credential.fullName.indexOf(" ");
            //             firstname = space > 0 ? credential.fullName.substr(0, credential.fullName.indexOf(" ")).trim() : credential.fullName;
            //             lastname = space > 0 ? credential.fullName.substr(credential.fullName.indexOf(" ") + 1).trim() : "";
            //         }
            //         this.rpcService.AppleAuth({
            //             id_token: credential.identityToken,
            //             push_token: pushToken,
            //             firstname: firstname,
            //             lastname: lastname
            //         }).then(this.handleAccessTokenResult(UserAuthType.Apple, null, resolve, reject),
            //             (error) => { reject(error); }
            //         );
            //     } else {
                    resolve({
                        success: false,
                        validated: false
                    });
            //     }
            // })
            //     .catch((err) => {
            //         Log.debug(Log.Tag.HttpUserProfileService, "Error signing in: " + err);
            //         resolve({
            //             success: false,
            //             validated: false
            //         });
            //     });
        });
    }

    forgotPassword(email: string): Promise<SuccessResult> {
        if (!email) {
            return Promise.resolve({ success: false });
        }

        return new Promise((resolve, reject) => {
            this.rpcService.ForgotPassword({
                username: email
            }).then((result) => {
                resolve({ success: true });
            }, (error) => { reject(error); }
            );
        });
    }

    resetPassword(email: string, code: string, newPassword: string): Promise<UserLoginResult> {
        return new Promise((resolve, reject) => {
            this.rpcService.ResetPassword({
                username: email,
                code: code,
                password: newPassword
            }).then(this.handleAccessTokenResult(UserAuthType.WhutsFree, email, resolve, reject),
                (error) => { reject(error); }
            );
        });
    }

    register(firstName: string, lastName: string, email: string, password: string, pushToken: string): Promise<UserLoginResult> {
        return new Promise((resolve, reject) => {
            this.rpcService.CreateAccount({
                username: email,
                password: password,
                firstname: firstName,
                lastname: lastName,
                push_token: pushToken
            }).then((result) => {
                this.updateUserProperty(UserProperty.Authenticated, !result.access_token ? "false" : "true");
                this.storageService.storeUserInfo({
                    auth: UserAuthType.WhutsFree,
                    authToken: result.access_token,
                    id: null,
                    needsValidation: !result.access_token,
                    firstName,
                    lastName,
                    email,
                    photoUrl: null
                }, this.settings.env);

                resolve({
                    success: true,
                    validated: result.verified
                });
            }, (error) => {
                reject(error);
            });
        });
    }

    resendValidationCode(email: string): Promise<SuccessResult> {
        return new Promise((resolve, reject) => {
            this.rpcService.ResendToken({ username: email }).then((result) => {
                resolve({ success: true });
            }, (error) => { reject(error); }
            );
        });
    }

    login(email: string, password: string, pushToken: string): Promise<UserLoginResult> {
        return new Promise((resolve, reject) => {
            this.rpcService.GetToken({
                username: email,
                password: password,
                push_token: pushToken
            }).then(
                this.handleAccessTokenResult(UserAuthType.WhutsFree, email, resolve, reject),
                (error) => { reject(error); }
            );
        });
    }

    validate(validationCode: string): Promise<SuccessResult> {
        // /user/verifyaccount/:verify_code/:username/:device
        const userInfo = this.userInfo;
        if (!userInfo || !userInfo.email || !validationCode) {
            this.updateUserProperty(UserProperty.Authenticated, "false");
            return Promise.resolve({ success: false });
        }

        return new Promise((resolve, reject) => {
            this.rpcService.VerifyAccount({ username: userInfo.email, code: validationCode }).then(
                this.handleAccessTokenResult(UserAuthType.WhutsFree, userInfo.email, resolve, reject),
                (error) => { reject(error); }
            );
        });
    }

    abortValidation(): void {
        this.updateUserProperty(UserProperty.Authenticated, "false");
        this.storageService.storeUserInfo(null, this.settings.env);
    }

    logout(): void {
        // SocialLogin.logout(() => {
        //     // logout
        // });
        this.updateUserProperty(UserProperty.Authenticated, "false");
        this.storageService.storeUserInfo(null, this.settings.env);
    }

    setFirstRedemption(): UserInfo {
        const userInfo: UserInfo = this.storageService.loadUserInfo(this.settings.env);
        if (userInfo && userInfo.authToken) {
            if (!userInfo.firstRedeemDate) {
                this.rpcService.FirstUse_queue();
                userInfo.firstRedeemDate = toDateStr(this.settings.calculateCurrentTime());
                this.storageService.storeUserInfo(userInfo, this.settings.env);
                return userInfo;
            } else {
                return userInfo;
            }
        } else {
            return null;
        }
    }

    contactUs(reason: "support"|"merchants"|"sponsors", firstName: string, lastName: string, email: string, content: string): Promise<SuccessResult> {
        return new Promise((resolve, reject) => {
            const deviceID = this.getDeviceId();
            let os = "";
            // const os = "[" + device.model + "/" + device.manufacturer + "] " + device.deviceType + " - " + device.os + " v" + device.osVersion + " SDK " + device.sdkVersion + " (" + device.region + "/" + device.language + ")";
            let version = "";
            // if (isAndroid) {
            //     const PackageManager = android.content.pm.PackageManager;
            //     const pkg = application.android.context.getPackageManager().getPackageInfo(application.android.context.getPackageName(), PackageManager.GET_META_DATA);
            //     const versionName = pkg.versionName;
            //     const versionCode = java.lang.Integer.toString(pkg.versionCode);
            //     version = versionName + " b" + versionCode;
            // } else {
            //     version = NSBundle.mainBundle.objectForInfoDictionaryKey("CFBundleShortVersionString");
            //     const buildVersion = NSBundle.mainBundle.objectForInfoDictionaryKey("CFBundleVersion");
            //     version = version + " b" + buildVersion;
            // }
            let logText = "";
            if (reason === "support" && this.settings.enableLogging) {
                logText = Log.getLogBufferText();
            }

            this.rpcService.ContactUs(
                {
                    from: "App", // placeholder
                    topic: "about_" + reason, // reason
                    firstName: firstName, // firstName
                    lastName: lastName, // lastName
                    email: email, // email
                    content: content, // message content
                    deviceID: deviceID, // ID
                    os: os, // OS
                    version: version, // version
                    logText: logText // log
                }
            ).then((data) => {
                resolve({
                    success: true
                });
            }, (err) => {
                resolve({
                    success: false
                });
            }).catch((err) => {
                resolve({
                    success: false
                });
            });
        });
    }

    private getDeviceId(): string {
        return "";
        // return uuidPlugin.getUUID();
    }

    private initUserInfo(): Promise<UserInfo> {
        let userInfo: UserInfo = this.storageService.loadUserInfo(this.settings.env);
        Log.debug(Log.Tag.HttpUserProfileService, "initUserInfo");
        Log.debug(Log.Tag.HttpUserProfileService, userInfo);
        if (userInfo && userInfo.authToken) {
            return new Promise((resolve, reject) => {
                this.rpcService.UserInfo().then((result: UserInfoRPC) => {
                    Log.trace(Log.Tag.HttpUserProfileService, "rpc load user info");
                    Log.trace(Log.Tag.HttpUserProfileService, result);
                    userInfo = this.storageService.loadUserInfo(this.settings.env);
                    userInfo.firstName = result.firstName;
                    userInfo.lastName = result.lastName;
                    userInfo.id = result.user_id;
                    userInfo.email = result.username;
                    userInfo.firstRedeemDate = result.first_use ? toDateStr(new Date()) : null;
                    this.storageService.storeUserInfo(userInfo, this.settings.env);
                    this.initSettings(userInfo);
                    resolve(userInfo);
                }, (error) => {
                    reject();
                });
            });
        } else {
            return Promise.reject();
        }
    }
    private handleLoginResult(authType: UserAuthType, email: string, resolve, reject) {
        return (loginResult: any) => {
            if (loginResult.success) {
                const userInfo = {
                    auth: authType,
                    authToken: loginResult.access_token ? loginResult.access_token : loginResult.auth_token,
                    id: null,
                    needsValidation: !loginResult.access_token && !loginResult.auth_token,
                    firstName: null,
                    lastName: null,
                    email: email,
                    photoUrl: null
                };
                this.storageService.storeUserInfo(userInfo, this.settings.env);
                this.initUserInfo().then((info) => {
                    Log.trace(Log.Tag.HttpUserProfileService, "info");
                    Log.trace(Log.Tag.HttpUserProfileService, info);
                    this.initSettings(userInfo);
                    this.updateUserProperty(UserProperty.Authenticated, this.needsValidation() ? "false" : "true");
                    resolve({
                        success: true,
                        validated: !this.needsValidation()
                    });
                }, (error) => {
                    this.updateUserProperty(UserProperty.Authenticated, "false");
                    reject(error);
                });
            } else {
                this.updateUserProperty(UserProperty.Authenticated, "false");
                resolve({
                    success: false,
                    validated: false
                });
            }
        };
    }

    private handleAccessTokenResult(authType: UserAuthType, email: string, resolve, reject) {
        return (accessToken: AccessToken) => {
            const userInfo = {
                auth: authType,
                authToken: accessToken.access_token,
                id: null,
                needsValidation: !accessToken.verified,
                firstName: null,
                lastName: null,
                email: email,
                photoUrl: null
            };
            this.storageService.storeUserInfo(userInfo, this.settings.env);
            if (userInfo.authToken) {
                this.updateUserProperty(UserProperty.Authenticated, "true");
                this.initUserInfo().then((info) => {
                    Log.trace(Log.Tag.HttpUserProfileService, "info");
                    Log.trace(Log.Tag.HttpUserProfileService, info);
                    this.initSettings(userInfo);
                    try {
                      OneSignal.User.addEmail(userInfo.email);
                      OneSignal.User.addAlias("name", userInfo.firstName + " " + userInfo.lastName);
                      OneSignal.User.addAlias("id", userInfo.id.toString());
                      OneSignal.login(userInfo.id.toString());
                    } catch (e) {
                      console.log("error logging into OneSignal: " + e);
                    }
                    resolve({
                        success: true,
                        validated: !this.needsValidation()
                    });
                }, (error) => {
                    reject(error);
                });
            } else if (!accessToken.verified) {
                this.updateUserProperty(UserProperty.Authenticated, "false");
                resolve({
                    success: true,
                    validated: false
                });
            } else {
                reject("Invalid response from GetToken");
            }
        };
    }

    private initSettings(info: UserInfo) {
        let changed = false;
        let settings = this.storageService.loadSettings();
        if (info && info.email && info.email.indexOf("whutsfree.com") > 0) {
            if (!settings) {
                settings = new Settings();
                settings.bounceBackNotificationOverride = true;
                settings.sponsorVideoSkipDelaySeconds = 2;
                settings.bounceBackNotificationDelaySeconds = 60;
            }
            settings.testable = true;
            changed = true;
        }
        if (changed) {
            this.storageService.storeSettings(settings);
        }
    }
}
